import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createUserWithEmailAndPassword,
  signOut,
  signInWithEmailAndPassword,
} from "firebase/auth";
import {
  FIREBASE_AUTH,
  FIREBASE_DB,
  USE_LOCAL_DEMO_AUTH,
} from "../../../firebaseConfig";
import { doc, onSnapshot } from "firebase/firestore";
import { getPostsByUser } from "./postSlice";
import { User } from "../../../types";
import { DEMO_EMAIL, DEMO_PASSWORD, demoUser } from "../../demo/localDemo";
import { Platform } from "react-native";
import { isDesktopEmbeddedWeb } from "../../utils/webViewMode";
import {
  clearPaidAccountSession,
  createPaidAccountSession,
  fetchPaidAccountSession,
  registerPaidAccount,
  type PaidAccount,
} from "../../services/videoServer";

const LOCAL_DEMO_SIGNED_IN_KEY = "localDemoSignedIn";
const LOCAL_MEMBER_USER_KEY = "kirkwatLocalMemberUser";

function getLocalDemoSignedIn() {
  if (Platform.OS !== "web") {
    return true;
  }

  // The short-video surface is an iframe within the already accessible
  // desktop site. Give that isolated app instance a non-persistent guest
  // identity so it opens the feed instead of presenting a second login gate.
  if (isDesktopEmbeddedWeb()) {
    return true;
  }

  return globalThis.localStorage?.getItem(LOCAL_DEMO_SIGNED_IN_KEY) === "true";
}

function localStoredMemberUser() {
  if (Platform.OS !== "web") return demoUser;
  try {
    const value = JSON.parse(
      globalThis.localStorage?.getItem(LOCAL_MEMBER_USER_KEY) || "null",
    );
    return value?.uid && value?.email ? (value as User) : demoUser;
  } catch {
    return demoUser;
  }
}

export function persistLocalAuthenticatedUser(user: User | null = demoUser) {
  if (Platform.OS === "web") {
    if (!user) {
      globalThis.localStorage?.removeItem(LOCAL_DEMO_SIGNED_IN_KEY);
      globalThis.localStorage?.removeItem(LOCAL_MEMBER_USER_KEY);
      return;
    }
    globalThis.localStorage?.setItem(LOCAL_DEMO_SIGNED_IN_KEY, "true");
    globalThis.localStorage?.setItem(LOCAL_MEMBER_USER_KEY, JSON.stringify(user));
  }
}

function userFromPaidAccount(account: PaidAccount): User {
  return {
    uid: account.id,
    email: account.email,
    displayName:
      account.label || account.email.split("@")[0] || "注册会员",
    photoURL: "",
    followingCount: 0,
    followersCount: 0,
    likesCount: 0,
  };
}

export const userAuthStateListener = createAsyncThunk(
  "auth/userAuthStateListener",
  async (_, { dispatch }) => {
    if (USE_LOCAL_DEMO_AUTH) {
      const cachedUser = getLocalDemoSignedIn()
        ? localStoredMemberUser()
        : null;
      dispatch(
        setUserState({
          currentUser: cachedUser,
          loaded: true,
        }),
      );

      // The HttpOnly server cookie is the source of truth. Restore the UI from
      // it after a rebuild or local-storage loss, but retain the cached identity
      // if a deployment briefly makes the API unavailable.
      try {
        const account = await fetchPaidAccountSession();
        if (account) {
          const user = userFromPaidAccount(account);
          persistLocalAuthenticatedUser(user);
          dispatch(setUserState({ currentUser: user, loaded: true }));
        } else if (!isDesktopEmbeddedWeb()) {
          persistLocalAuthenticatedUser(null);
          dispatch(setUserState({ currentUser: null, loaded: true }));
        }
      } catch {
        // Network errors and rolling deployments are not logout events.
      }
      return;
    }

    FIREBASE_AUTH.onAuthStateChanged((user) => {
      if (user) {
        dispatch(getCurrentUserData());
        dispatch(getPostsByUser(user.uid));
      } else {
        dispatch(setUserState({ currentUser: null, loaded: true }));
      }
    });
  },
);

export const getCurrentUserData = createAsyncThunk(
  "auth/getCurrentUserData",
  async (_, { dispatch }) => {
    if (FIREBASE_AUTH.currentUser) {
      const unsub = onSnapshot(
        doc(FIREBASE_DB, "user", FIREBASE_AUTH.currentUser.uid),
        (res) => {
          if (res.exists()) {
            dispatch(setUserState({ currentUser: res.data(), loaded: true }));
          }
        },
      );
    } else {
      console.log("No user is signed in.");
    }
  },
);

export const login = createAsyncThunk(
  "auth/login",
  async (payload: { email: string; password: string }, { dispatch }) => {
    const { email, password } = payload;
    if (USE_LOCAL_DEMO_AUTH) {
      if (email === DEMO_EMAIL && password === DEMO_PASSWORD) {
        persistLocalAuthenticatedUser(demoUser);
        dispatch(setUserState({ currentUser: demoUser, loaded: true }));
        return;
      }
      const session = await createPaidAccountSession(email.trim(), password);
      if (!session.account) {
        throw new Error("账号验证失败，请检查邮箱和密码");
      }
      const user = userFromPaidAccount(session.account);
      persistLocalAuthenticatedUser(user);
      dispatch(setUserState({ currentUser: user, loaded: true }));
      return;
    }
    await signInWithEmailAndPassword(FIREBASE_AUTH, email, password);
  },
);

export const register = createAsyncThunk(
  "auth/register",
  async (payload: { email: string; password: string }, { dispatch }) => {
    const { email, password } = payload;
    if (USE_LOCAL_DEMO_AUTH) {
      const session = await registerPaidAccount(email.trim(), password);
      if (!session.account) throw new Error("注册未完成，请稍后重试");
      const user = userFromPaidAccount(session.account);
      persistLocalAuthenticatedUser(user);
      dispatch(setUserState({ currentUser: user, loaded: true }));
      return;
    }
    await createUserWithEmailAndPassword(FIREBASE_AUTH, email, password);
  },
);

export const logout = createAsyncThunk(
  "auth/logout",
  async (_, { dispatch }) => {
    try {
      await clearPaidAccountSession();
    } catch {
      // Local state still needs to be cleared if the session API is offline.
    }
    if (USE_LOCAL_DEMO_AUTH) {
      persistLocalAuthenticatedUser(null);
    } else {
      await signOut(FIREBASE_AUTH);
    }

    dispatch(setUserState({ currentUser: null, loaded: true }));
  },
);

interface AuthState {
  currentUser: User | null;
  loaded: boolean;
}

const initialState: AuthState = {
  currentUser: null,
  loaded: false,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setUserState: (state, action) => {
      state.currentUser = action.payload.currentUser;
      state.loaded = action.payload.loaded;
    },
  },
  extraReducers: (builder) => {
    // Handle additional cases for async actions if needed
  },
});

export const { setUserState } = authSlice.actions;
export default authSlice.reducer;
